import { currentSite } from '@src/app/utils/domainHelpers';
import { ServerConfig } from '@superbet-group/web.lib.config';
import {
    loadScript,
    logger,
} from '@superbet-group/web.lib.utilities';
import {
    Site,
    WafSupportedDomains,
    WafUrlConfig,
} from '@superbet-group/web.types';

type WafIntegration = {
    hasToken: () => boolean;
    getToken: () => Promise<string>;
};

class WafService {
    private wafIntegration: WafIntegration;
    private isEnabled = false;
    private wafUrl = '';
    private wafFallbackUrl = '';
    private isLoaded = false;
    private loadingPromise: Promise<unknown> | null = null;

    constructor() {}

    public initialize(config: ServerConfig): void {
        // When the service is running under Meticulous tests, we don't want non-determinism,
        // and all regular calls to backends are stubbed.
        const isRunningInMeticulousTest = !!(window as any)?.Meticulous?.isRunningAsTest;
        this.wafUrl = WafService.parseUrl(config.auth.awsWafUrl);
        this.wafFallbackUrl = WafService.parseUrl(config.auth.awsWafFallbackUrl);
        this.isEnabled = !!config.auth.isAwsWafIntegrationEnabled && !!this.wafUrl && !isRunningInMeticulousTest;
    }

    public async maybeLoadWafScript(): Promise<void> {
        if (!this.isEnabled || this.isLoaded || this.loadingPromise) {
            return;
        }
        // eslint-disable-next-line no-async-promise-executor
        this.loadingPromise = new Promise<void>(async (resolve) => {
            try {
                await loadScript(this.wafUrl, 'waf-integration');
            } catch (e) {
                if (this.wafFallbackUrl) {
                    await loadScript(this.wafFallbackUrl, 'waf-integration-fallback');
                }
            } finally {
                // eslint-disable-next-line no-promise-executor-return
                await new Promise((resolve) => setTimeout(resolve, 10));
                this.wafIntegration = window.AwsWafIntegration!;
                this.isLoaded = !!this.wafIntegration;
                resolve();
            }
        });

        await this.loadingPromise;
    }

    public async maybeWaitForScriptLoaded(): Promise<void> {
        if (!this.isEnabled || this.isLoaded || !this.loadingPromise) {
            return;
        }

        await this.loadingPromise;
    }

    public async maybeWaitForToken(): Promise<void> {
        if (!this.isEnabled || this.hasToken()) {
            return;
        }
        await this.maybeGetToken();
    }

    public hasToken(): boolean {
        return !!this.wafIntegration?.hasToken();
    }

    public async maybeGetToken(): Promise<string | null> {
        if (!this.isEnabled) {
            return null;
        }

        try {
            return await this.wafIntegration.getToken();
        } catch (e) {
            logger.error(new Error('Failed to get WAF token'));
            return null;
        }
    }

    private static parseUrl(wafUrlConfig: WafUrlConfig): string {
        if (typeof wafUrlConfig === 'string') {
            return wafUrlConfig;
        }

        let site: WafSupportedDomains = currentSite;

        // Hack for .bet because it's hacked to com in getCurrentSite
        if (currentSite === Site.SuperbetCom && window.location.hostname.includes('superbet.bet')) {
            site = 'SuperbetBet';
        }

        return wafUrlConfig[site] || wafUrlConfig.Fallback || '';
    }
}

export const wafService = new WafService();
